import { Timeline, type TimelineItem } from "@/components/ui/timeline";
import { useProjectTimeline } from "@/features/projects/hooks/useProjectTimeline";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/timeline/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { timelineEvents, workerStats, loading } = useProjectTimeline(projectId);

  // Transform real timeline events to the TimelineItem format for the Timeline component
  const timelineItems: TimelineItem[] = useMemo(() => {
    // Group events by date
    const eventsByDate = new Map<string, typeof timelineEvents>();
    
    for (const event of timelineEvents) {
      const dateKey = event.timestamp.toDate().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const existing = eventsByDate.get(dateKey) || [];
      eventsByDate.set(dateKey, [...existing, event]);
    }

    // Convert to TimelineItems
    return Array.from(eventsByDate.entries()).map(([dateKey, events], index) => {
      const isPlanned = events.some((e) => e.type === "planned");
      const totalForDate = events[0]?.totalWorkers ?? 0;
      
      // Calculate net change for the date
      const netChange = events.reduce((sum, e) => sum + (e.workerCountChange || 0), 0);

      return {
        id: `${dateKey}-${index}`,
        title: isPlanned
          ? `${dateKey} - Planned`
          : `${dateKey} Total: ${totalForDate}`,
        time: "",
        dotColor: isPlanned ? "#ffffff" : "#3b82f6",
        dotClassName: isPlanned
          ? "border-2 border-purple-400 bg-white w-[18px] h-[18px]"
          : "w-[18px] h-[18px]",
        description: isPlanned ? `${events.length} Workers` : "",
        content: (
          <div className="flex flex-col gap-2">
            {events.map((event, idx) => {
              const workUnitName = event.metadata.workUnitId
                ? event.title.split(" - ")[0] || "Work Unit"
                : event.title;
              
              return (
                <div key={idx} className="flex items-center gap-2">
                  <span
                    className={`rounded border border-gray-400 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 ${
                      event.type === "workers_removed" || event.type === "work_unit_deleted"
                        ? "line-through decoration-gray-400"
                        : ""
                    }`}
                  >
                    {workUnitName}
                  </span>
                  <span className="text-sm text-gray-900">{event.description}</span>
                </div>
              );
            })}
            
            {/* Show net change badges */}
            {netChange !== 0 && (
              <div className="mt-1 flex items-center gap-2">
                {netChange < 0 && (
                  <span className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-500">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9V3M6 9L3 6M6 9L9 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {netChange}
                  </span>
                )}
                {netChange > 0 && (
                  <span className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-600">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 3V9M6 3L3 6M6 3L9 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    +{netChange}
                  </span>
                )}
              </div>
            )}
            
            {/* Show shortage badge for planned events */}
            {isPlanned && events.some((e) => e.metadata.workUnitId) && (
              <div className="inline-flex">
                <span className="rounded bg-orange-100 px-2 py-1 text-xs font-medium text-orange-600">
                  Check Shortage
                </span>
              </div>
            )}
          </div>
        ),
      };
    });
  }, [timelineEvents]);

  // Calculate shortage
  const totalWorkersNeeded = workerStats.workersNeeded + workerStats.plannedWorkersNeeded;
  const totalAssigned = workerStats.currentTotal + workerStats.plannedTotal;
  const shortage = Math.max(0, totalWorkersNeeded - totalAssigned);

  if (loading) {
    return (
      <div className="flex flex-col">
        <div className="mt-4">
          <div className="rounded border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
              <span className="ml-3 text-sm text-gray-600">Loading timeline...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (timelineItems.length === 0) {
    return (
      <div className="flex flex-col">
        <div className="mt-4">
          <div className="rounded border border-gray-200 bg-white p-4">
            <div className="space-y-3 py-8 text-center">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">No Timeline Events</p>
                <p className="text-sm text-gray-500">
                  Project activity will appear here as changes are made
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Tab Content */}
      <div className="mt-4">
        <div className="rounded border border-gray-200 bg-white p-4">
          {/* Timeline Header Stats */}
          <div className="mb-4 grid grid-cols-1 gap-3 min-[744px]:grid-cols-3 min-[744px]:gap-4">
            <div className="rounded border border-gray-200 bg-white p-4">
              <div className="mb-2 text-xs text-gray-500">Active workers across all work units</div>
              <div className="text-2xl font-bold text-gray-900">
                Now total <span className="text-blue-600">{workerStats.currentTotal}</span>
              </div>
            </div>
            <div className="rounded border border-gray-200 bg-white p-4">
              <div className="mb-2 text-xs text-gray-500">Assigned with future start dates</div>
              <div className="text-2xl font-bold text-gray-900">
                Planned <span className="text-purple-600">{workerStats.plannedTotal}</span>
              </div>
            </div>
            <div className="rounded border border-gray-200 bg-white p-4">
              <div className="mb-2 text-xs text-gray-500">
                {totalWorkersNeeded} needed, {totalAssigned} assigned
              </div>
              <div className="text-2xl font-bold text-gray-900">
                Short <span className="text-orange-600">{shortage}</span>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mb-4 rounded border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-3 min-[744px]:flex-nowrap min-[744px]:gap-6">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                <span className="text-xs text-gray-700">Actual workers</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full border-2 border-purple-400 bg-white"></div>
                <span className="text-xs text-gray-700">Planned workers</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-500">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 9V3M6 9L3 6M6 9L9 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Decrease
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-600">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 3V9M6 3L3 6M6 3L9 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Increase
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-orange-100 px-2 py-1 text-xs font-medium text-orange-600">
                  Short
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <Timeline items={timelineItems} alternate={true} dotSize="md" lineColor="#d1d5db" />
        </div>
      </div>
    </div>
  );
}
